import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Link2, Pencil, Plus, Send, Trash2, MoreVertical, X } from "lucide-react";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Link, useSearchParams } from "react-router-dom";
import { useNavbar } from "./DashboardLayout";
import ApplicationWorkspace from "../components/applicationWorkspace";
import AnswerBank from "../components/AnswerBank";
import { HEADER_ICON_BUTTON, TopBarToggle, TrackerSearch } from "../components/trackerHeader";
import IconButtonWithTooltip from "../components/IconButtonWithTooltip";
import { CompanyLogo } from "../components/dreamShared";
import { DEADLINE_TAG, MENU_ITEM, OUTLINE_TAG, TAG } from "../components/applicationShared";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";
import { DatePicker } from "../components/ui/date-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  MELT_BUTTON,
  MELT_BUTTON_QUIET,
  MELT_DIALOG,
  MELT_INPUT,
  MELT_LABEL,
  MELT_REQUIRED,
} from "../components/ui/melt";
import {
  STAGES,
  brandOf,
  byDeadline,
  deadlineLabel,
  draftCard,
  draftFromSaved,
  effortLabel,
  groupRows,
  isUrgent,
  movedThisWeek,
  shortDate,
  toMs,
  type Draft,
  type DraftQuestion,
  type DraftRow,
  type DraftStage,
} from "../utils/drafts";
import { lookupJob } from "../utils/draftsApi";
import { buildBank, type BankDoc } from "../utils/answerBank";
import { forgetRole, markRoleApplied } from "../utils/newRolesApi";
import type { RoleAction } from "../utils/newRoles";
import { normalizeCompany, type DreamCompany } from "../utils/dreamCompanies";
import { cn } from "../lib/utils";

// Drafts: every job saved to apply to, from a pasted link, by hand, or saved
// from New roles on Home. One list, closest deadline first; a status chip on
// each row says how far the written part is (Drafting, Ready to submit, Saved). "Mark applied" moves a job
// to Applications (utils/drafts.ts). The Answer bank tab (?view=answers) collects
// what's been written (components/AnswerBank.tsx).

type View = "jobs" | "answers";

const GRID =
  "grid grid-cols-[minmax(220px,2.2fr)_118px_120px_128px_24px] items-center gap-4";

/** Links compare without scheme, www, query or trailing slash */
const linkKey = (url?: string) =>
  (url || "")
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, "")
    .replace(/^www\./, "")
    .split(/[?#]/)[0]
    .replace(/\/+$/, "");

// ---------------------------------------------------------------- save a job

type Pending = {
  url: string;
  company: string;
  role: string;
  deadline: string;
  location: string;
  domain?: string;
  logo?: string;
  /** The lookup failed or found nothing: say so above the fields */
  note?: string;
};

/**
 * The link icon in the top bar: opens into a field like search does. Enter reads
 * the link (backend /jobs/lookup) and hands what it found to the confirm card.
 * Keeps its own state, like TrackerSearch: the top bar renders a beat after the page.
 */
function SaveLink({ onFound }: { onFound: (pending: Pending) => void }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState("");
  const [looking, setLooking] = useState(false);
  const [error, setError] = useState("");

  const close = () => {
    setOpen(false);
    setLink("");
    setError("");
  };

  const look = async () => {
    const url = link.trim();
    if (!url || looking) return;
    setError("");
    setLooking(true);
    try {
      const found = await lookupJob(url);
      onFound({
        url: found.url,
        company: found.company,
        role: found.role,
        deadline: found.deadline,
        location: found.location,
        domain: found.domain,
        logo: found.logo,
        note: found.company && found.role ? undefined : "Couldn’t read everything from that page. Fill in the rest.",
      });
      close();
    } catch (e: any) {
      if (e?.message === "not a link") {
        setError("That doesn’t look like a job link");
      } else {
        // Backend asleep or offline: they can still type it in
        console.error("Job lookup failed:", e);
        onFound({ url, company: "", role: "", deadline: "", location: "", note: "Couldn’t open that link. Fill in the details." });
        close();
      }
    } finally {
      setLooking(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        look();
      }}
      className={cn(
        "flex h-9 items-center overflow-hidden rounded-[9px] transition-[width,background-color] duration-[220ms] ease-out",
        open ? "w-72 bg-melt-text1/[.05]" : "w-9",
        error && "shadow-[inset_0_0_0_1px_rgba(165,73,46,.45)]"
      )}
    >
      <IconButtonWithTooltip
        label="Save a job from its link"
        onClick={() => setOpen(true)}
        className={cn(HEADER_ICON_BUTTON, open && "hover:bg-transparent")}
      >
        <Link2 size={17} strokeWidth={1.75} />
      </IconButtonWithTooltip>
      {open && (
        <>
          <input
            autoFocus
            type="text"
            inputMode="url"
            value={link}
            onChange={(e) => {
              setLink(e.target.value);
              setError("");
            }}
            onBlur={() => !link.trim() && !looking && close()}
            onKeyDown={(e) => e.key === "Escape" && close()}
            disabled={looking}
            placeholder={error || "Paste a job link, then Enter"}
            title={error || undefined}
            aria-label="Job link"
            className={cn(
              "min-w-0 flex-1 bg-transparent pr-2 text-sm text-melt-text1 outline-none placeholder:text-melt-text3 disabled:opacity-60",
              error && "placeholder:text-[#A5492E]"
            )}
          />
          {looking && <span className="mr-3 flex-none font-plex text-[11px] text-melt-text3">reading…</span>}
        </>
      )}
    </form>
  );
}

/** What the link said, to check and fix before it's saved. Shown above the list while confirming. */
function ConfirmJob({
  pending,
  drafts,
  onChange,
  onCancel,
  onSaved,
}: {
  pending: Pending;
  drafts: Draft[];
  onChange: (pending: Pending) => void;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const already = useMemo(() => {
    const key = linkKey(pending.url);
    return key ? drafts.find((d) => d.status !== "completed" && linkKey(d.jobUrl) === key) : undefined;
  }, [drafts, pending.url]);

  const save = async () => {
    const user = auth.currentUser;
    if (!user || !pending.company.trim() || !pending.role.trim() || saving) return;
    setSaving(true);
    setError("");
    try {
      await addDoc(collection(db, "users", user.uid, "writtenApplications"), {
        company: pending.company.trim(),
        role: pending.role.trim(),
        jobUrl: pending.url,
        ...(pending.deadline ? { deadline: pending.deadline } : {}),
        ...(pending.location.trim() ? { location: pending.location.trim() } : {}),
        ...(pending.domain ? { domain: pending.domain } : {}),
        ...(pending.logo ? { logo: pending.logo } : {}),
        source: "pasted",
        status: "saved",
        createdAt: serverTimestamp(),
      });
      onSaved();
    } catch (e) {
      console.error("Error saving job:", e);
      setError("Couldn’t save. Try again.");
      setSaving(false);
    }
  };

  const set = (field: keyof Pending) => (value: string) => onChange({ ...pending, [field]: value });

  return (
    <section className="rounded-[14px] border border-melt-text1/[.12] bg-white px-4 pb-4 pt-3.5">
      <div className="mb-3 flex min-w-0 items-center gap-2 text-[13px] text-melt-text3">
        <Link2 size={14} strokeWidth={1.75} className="flex-none" />
        <span className="truncate">{pending.url}</span>
      </div>
      {pending.note && <p className="mb-3 text-[13px] text-melt-text2">{pending.note}</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1.4fr_170px]">
        <label className="flex flex-col gap-1.5">
          <span className={MELT_LABEL}>
            Company<span className={MELT_REQUIRED}>*</span>
          </span>
          <input value={pending.company} onChange={(e) => set("company")(e.target.value)} placeholder="Company" className={MELT_INPUT} autoFocus={!pending.company} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={MELT_LABEL}>
            Role<span className={MELT_REQUIRED}>*</span>
          </span>
          <input value={pending.role} onChange={(e) => set("role")(e.target.value)} placeholder="e.g. Software Engineer Intern" className={MELT_INPUT} />
        </label>
        <div className="flex flex-col gap-1.5">
          <span className={MELT_LABEL}>Deadline</span>
          <DatePicker value={pending.deadline} onChange={set("deadline")} className={MELT_INPUT} placeholder="No deadline" aria-label="Deadline" />
        </div>
      </div>
      <div className="mt-3.5 flex flex-wrap items-center justify-end gap-2">
        {(error || already) && (
          <span className={cn("mr-auto text-[13px]", error ? "text-[#A5492E]" : "text-melt-text2")}>
            {error || `Already saved: ${already!.company}, ${already!.role}`}
          </span>
        )}
        <button type="button" onClick={onCancel} className={cn(MELT_BUTTON_QUIET, "h-9 text-sm")}>
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !pending.company.trim() || !pending.role.trim()}
          className={cn(MELT_BUTTON, "h-9 rounded-[10px] px-4 text-sm")}
        >
          {saving ? "Saving…" : "Save job"}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- rows

function RowMenu({
  row,
  onEdit,
  onApplied,
  onRemove,
}: {
  row: DraftRow;
  onEdit?: () => void;
  onApplied: () => void;
  onRemove: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`Options for ${row.company}`}
          className="grid h-6 w-6 flex-none place-items-center rounded-[7px] text-melt-text3 opacity-0 outline-none transition-opacity hover:bg-melt-text1/[.07] hover:text-melt-text1 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-melt-caramel/30 group-hover:opacity-100 data-[state=open]:bg-melt-text1/[.07] data-[state=open]:opacity-100"
        >
          <MoreVertical size={15} strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48 rounded-[11px] border-melt-hair bg-melt-menu p-[5px] font-grotesk shadow-[0_18px_34px_-18px_rgba(92,52,23,.5)]"
      >
        {row.url && (
          <DropdownMenuItem asChild className={MENU_ITEM}>
            <a href={row.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={15} strokeWidth={1.75} />
              Open posting
            </a>
          </DropdownMenuItem>
        )}
        {onEdit && (
          <DropdownMenuItem onSelect={onEdit} className={MENU_ITEM}>
            <Pencil size={15} strokeWidth={1.75} />
            Edit
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onApplied} className={MENU_ITEM}>
          <Send size={15} strokeWidth={1.75} />
          Mark applied
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-melt-hair" />
        <DropdownMenuItem
          onSelect={onRemove}
          className={cn(MENU_ITEM, "text-[#A5492E] focus:bg-[#A5492E]/[.07] [&_svg]:text-[#A5492E]")}
        >
          <Trash2 size={15} strokeWidth={1.75} />
          {row.saved ? "Unsave" : "Remove"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DeadlineCell({ deadline }: { deadline?: string }) {
  const label = deadlineLabel(deadline);
  if (!label) return <span className="font-plex text-[11px] text-melt-text3">No deadline</span>;
  if (label === "Closed") return <span className={cn(OUTLINE_TAG, "w-fit text-melt-text3")}>Closed</span>;
  return (
    <span className={cn(isUrgent(deadline) ? cn(TAG, "bg-[#A5492E]/[.1] text-[#A5492E]") : DEADLINE_TAG, "w-fit whitespace-nowrap")}>
      {label}
    </span>
  );
}

type RowProps = {
  row: DraftRow;
  /** Its icon: from the link, or the Dream Company of that name */
  brand: { domain?: string; logo?: string };
  busy: boolean;
  onOpen: () => void;
  onEdit?: () => void;
  onApplied: () => void;
  onRemove: () => void;
};

/** The little status on the right: what the row is, instead of a section per stage */
const STATUS_CHIP: Record<DraftStage, string> = {
  drafting: "bg-melt-peach text-melt-amber",
  ready: "bg-melt-toffee/[.12] text-melt-toffee",
  saved: "border border-melt-text1/[.14] text-melt-text2",
};

function StatusChip({ stage, busy }: { stage: DraftStage; busy: boolean }) {
  const def = STAGES.find((s) => s.id === stage)!;
  return (
    <span className={cn(TAG, STATUS_CHIP[stage], "inline-flex w-fit items-center gap-1.5 whitespace-nowrap")} title={def.hint}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: def.color }} />
      {busy ? "Moving…" : def.title}
    </span>
  );
}

function Row({ row, brand, busy, onOpen, onEdit, onApplied, onRemove }: RowProps) {
  const effort = effortLabel(row);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open ${row.company}, ${row.role}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        GRID,
        "group cursor-pointer border-t border-melt-hair px-4 py-3 outline-none transition-colors hover:bg-melt-text1/[.025] focus-visible:bg-melt-text1/[.04]"
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <CompanyLogo name={row.company} domain={brand.domain} logo={brand.logo} size={32} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-melt-text1">{row.company}</p>
          <p className="truncate text-[13px] text-melt-text2">
            {row.role || "Role not added"}
            {row.location && <span className="text-melt-text3"> · {row.location}</span>}
          </p>
        </div>
      </div>

      <DeadlineCell deadline={row.deadline} />

      {effort ? (
        <span className={cn(OUTLINE_TAG, "w-fit whitespace-nowrap")}>{effort}</span>
      ) : (
        <span className="font-plex text-[11px] text-melt-text3">No questions</span>
      )}

      <StatusChip stage={row.stage} busy={busy} />

      {/* stopPropagation: the menu shouldn't also open the row */}
      <div className="flex justify-end" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <RowMenu row={row} onEdit={onEdit} onApplied={onApplied} onRemove={onRemove} />
      </div>
    </div>
  );
}

/** Every draft and saved job in one list, closest deadline first. */
function DraftList({
  rows,
  searching,
  onAdd,
  children,
}: {
  rows: DraftRow[];
  searching: boolean;
  onAdd: () => void;
  children: (row: DraftRow) => React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2.5 flex items-center gap-2 pr-1">
        <span className="h-2 w-2 rounded-[3px] bg-melt-caramel" />
        <h2 className="text-[13.5px] font-semibold text-melt-text1">Saved jobs</h2>
        <span className="font-plex text-xs text-melt-text3">{rows.length}</span>
        <button
          type="button"
          onClick={onAdd}
          aria-label="Add a job"
          title="Add a job"
          className="ml-auto grid h-6 w-6 place-items-center rounded-[7px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
        >
          <Plus size={16} strokeWidth={1.75} />
        </button>
      </div>

      <div className="scrollbar-thin overflow-x-auto rounded-[14px] border border-melt-text1/[.12] bg-melt-paper">
        <div className="min-w-[780px]">
          {rows.length === 0 ? (
            <p className="px-4 py-3.5 text-[13px] text-melt-text3">
              <span className="font-medium text-melt-text2">{searching ? "No matches" : "Nothing saved"}</span>
              {!searching && (
                <>
                  {" · Save one from its link up top, add one with +, or save roles from "}
                  <Link to="/dashboard/roles" className="font-medium text-melt-text1 underline underline-offset-2">
                    New roles
                  </Link>
                  .
                </>
              )}
            </p>
          ) : (
            <>
              <div className={cn(GRID, "px-4 py-2.5 font-plex text-[10.5px] font-medium uppercase tracking-[.1em] text-melt-text3")}>
                <span>Company</span>
                <span>Deadline</span>
                <span>Questions</span>
                <span>Status</span>
                <span />
              </div>
              {rows.map(children)}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- page

export default function Drafts() {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [questions, setQuestions] = useState<DraftQuestion[]>([]);
  const [savedRoles, setSavedRoles] = useState<RoleAction[]>([]);
  const [dreamCompanies, setDreamCompanies] = useState<DreamCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(auth.currentUser);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editDraft, setEditDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<Draft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  /** A pasted link being confirmed (SaveLink -> ConfirmJob) */
  const [pending, setPending] = useState<Pending | null>(null);
  const [bankDocs, setBankDocs] = useState<BankDoc[]>([]);
  /** The + on the Answer bank tab */
  const [addingAnswer, setAddingAnswer] = useState(false);
  const [params, setParams] = useSearchParams();
  const view: View = params.get("view") === "answers" ? "answers" : "jobs";
  const { setNavbarActions } = useNavbar();

  const switchView = (next: View) => {
    setSearch("");
    setPending(null);
    setParams(next === "answers" ? { view: "answers" } : {}, { replace: true });
  };

  useEffect(
    () =>
      onAuthStateChanged(auth, (currentUser) => {
        setUser(currentUser);
        if (!currentUser) setLoading(false);
      }),
    []
  );

  useEffect(() => {
    if (!user) {
      setDrafts([]);
      return;
    }
    const q = query(collection(db, "users", user.uid, "writtenApplications"), orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snapshot) => setDrafts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Draft)),
      (error) => console.error("Could not load drafts:", error)
    );
  }, [user]);

  useEffect(() => {
    if (!user) {
      setQuestions([]);
      return;
    }
    const q = query(collection(db, "users", user.uid, "applicationQuestions"), orderBy("order", "asc"));
    return onSnapshot(
      q,
      (snapshot) => {
        setQuestions(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as DraftQuestion));
        setLoading(false);
      },
      (error) => {
        console.error("Could not load questions:", error);
        setLoading(false);
      }
    );
  }, [user]);

  // Roles saved from New roles on Home
  useEffect(() => {
    if (!user) {
      setSavedRoles([]);
      return;
    }
    return onSnapshot(
      collection(db, "users", user.uid, "roleActions"),
      (snapshot) => setSavedRoles(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as RoleAction)),
      (error) => console.error("Could not load saved roles:", error)
    );
  }, [user]);

  // The answer bank's own answers and its copies of draft answers
  useEffect(() => {
    if (!user) {
      setBankDocs([]);
      return;
    }
    return onSnapshot(
      collection(db, "users", user.uid, "answerBank"),
      (snapshot) => setBankDocs(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as BankDoc)),
      (error) => console.error("Could not load answer bank:", error)
    );
  }, [user]);

  // Their websites give icons to jobs saved without one
  useEffect(() => {
    if (!user) {
      setDreamCompanies([]);
      return;
    }
    return onSnapshot(
      collection(db, "users", user.uid, "dreamCompanies"),
      (snapshot) => setDreamCompanies(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as DreamCompany)),
      (error) => console.error("Could not load dream companies:", error)
    );
  }, [user]);

  const dreamDomains = useMemo(
    () => new Map(dreamCompanies.filter((c) => c.domain).map((c) => [normalizeCompany(c.name), c.domain!] as [string, string])),
    [dreamCompanies]
  );
  const groups = useMemo(() => groupRows(drafts, questions, savedRoles, search), [drafts, questions, savedRoles, search]);
  const rows = useMemo(() => [...groups.drafting, ...groups.ready, ...groups.saved].sort(byDeadline), [groups]);
  const jobCount = useMemo(() => {
    const all = groupRows(drafts, questions, savedRoles, "");
    return all.drafting.length + all.ready.length + all.saved.length;
  }, [drafts, questions, savedRoles]);
  const bank = useMemo(() => buildBank(drafts, questions, bankDocs), [drafts, questions, bankDocs]);
  const past = useMemo(
    () => drafts.filter((d) => d.status === "completed").sort((a, b) => toMs(b.appliedAt) - toMs(a.appliedAt)),
    [drafts]
  );
  const moved = movedThisWeek(drafts);
  const openDraft = openId ? drafts.find((d) => d.id === openId) || null : null;

  // Title and tools sit in the top bar, like the tracker
  const header = (
    <div className="flex w-full min-w-0 items-center justify-between gap-4 font-grotesk">
      <div className="flex min-w-0 items-baseline gap-3">
        <h1 className="whitespace-nowrap text-[22px] font-semibold leading-none tracking-[-.03em] text-melt-text1">Drafts</h1>
      </div>
      {!openDraft && (
        <div className="flex items-center gap-2.5">
          {view === "jobs" ? (
            <>
              <TrackerSearch key="jobs" onQueryChange={setSearch} label="Search drafts" placeholder="Search company or role" />
              <SaveLink onFound={setPending} />
            </>
          ) : (
            <>
              <TrackerSearch key="answers" onQueryChange={setSearch} label="Search answers" placeholder="Search answers" />
              <IconButtonWithTooltip label="Add an answer" onClick={() => setAddingAnswer(true)} className={HEADER_ICON_BUTTON}>
                <Plus size={17} strokeWidth={1.75} />
              </IconButtonWithTooltip>
            </>
          )}
          <TopBarToggle
            value={view}
            onChange={switchView}
            options={[
              { value: "jobs", label: "Saved jobs", count: jobCount },
              { value: "answers", label: "Answer bank", count: bank.length },
            ]}
          />
        </div>
      )}
    </div>
  );

  useEffect(() => {
    // An open draft puts its own header up there (applicationWorkspace)
    if (openDraft) return;
    setNavbarActions(header);
    // `header` is rebuilt every render; these are the values it shows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNavbarActions, !!openDraft, view, jobCount, bank.length]);

  useEffect(() => () => setNavbarActions(null), [setNavbarActions]);

  // ---------------------------------------------------------------- writes

  /** Open a row: a saved job becomes a draft first. */
  const open = useCallback(
    async (row: DraftRow) => {
      if (!user) return;
      if (row.saved) {
        setBusy(row.key);
        try {
          const ref = await addDoc(collection(db, "users", user.uid, "writtenApplications"), {
            ...draftFromSaved(row.saved),
            createdAt: serverTimestamp(),
          });
          setOpenId(ref.id);
        } catch (e) {
          console.error("Could not start draft:", e);
          alert("Failed to start this one");
        } finally {
          setBusy(null);
        }
        return;
      }
      const draft = row.draft!;
      setOpenId(draft.id);
      if (draft.status === "saved") {
        updateDoc(doc(db, "users", user.uid, "writtenApplications", draft.id), { status: "in-progress" }).catch((e) =>
          console.error("Could not start draft:", e)
        );
      }
    },
    [user]
  );

  /** Mark applied: a tracker card (joined to one already there), and out of Drafts. */
  const markApplied = async (row: DraftRow) => {
    if (!user || busy) return;
    setBusy(row.key);
    try {
      if (row.saved) {
        const card = {
          ...draftCard({ id: row.saved.id, company: row.company, role: row.role, jobUrl: row.url, location: row.location, roleId: row.saved.id } as Draft),
          message_id: `feed_${row.saved.id}`,
        };
        await markRoleApplied(card);
        await deleteDoc(doc(db, "users", user.uid, "roleActions", row.saved.id));
        forgetRole(row.saved.id);
      } else {
        const draft = row.draft!;
        const { cardId } = await markRoleApplied(draftCard(draft));
        await updateDoc(doc(db, "users", user.uid, "writtenApplications", draft.id), {
          status: "completed",
          appliedAt: new Date().toISOString(),
          cardId,
        });
        if (draft.roleId) forgetRole(draft.roleId);
        if (openId === draft.id) setOpenId(null);
      }
    } catch (e) {
      console.error("Could not move to Applications:", e);
      alert("Couldn’t move it to Applications. Try again in a moment.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (draft: Draft) => {
    if (!user) return;
    setRemoving(null);
    const related = questions.filter((q) => q.applicationId === draft.id);
    try {
      await Promise.all([
        deleteDoc(doc(db, "users", user.uid, "writtenApplications", draft.id)),
        ...related.map((q) => deleteDoc(doc(db, "users", user.uid, "applicationQuestions", q.id))),
      ]);
      if (openId === draft.id) setOpenId(null);
    } catch (e) {
      console.error("Error deleting draft:", e);
      alert("Failed to remove");
    }
  };

  const unsave = (saved: RoleAction) =>
    user &&
    deleteDoc(doc(db, "users", user.uid, "roleActions", saved.id)).catch((e) => console.error("Could not unsave role:", e));

  // ---------------------------------------------------------------- render

  if (loading) {
    return <div className="flex items-center justify-center py-12 font-grotesk text-melt-text3">Loading…</div>;
  }

  if (openDraft) {
    const isPast = openDraft.status === "completed";
    return (
      <ApplicationWorkspace
        application={openDraft}
        questions={questions.filter((q) => q.applicationId === openDraft.id)}
        brand={brandOf(openDraft, dreamDomains)}
        submitted={isPast}
        submitting={busy === openDraft.id}
        onComplete={() => markApplied({ key: openDraft.id, draft: openDraft } as DraftRow)}
        onBack={() => setOpenId(null)}
      />
    );
  }

  if (view === "answers") {
    return (
      <div className="flex w-full flex-col pb-8 font-grotesk">
        <AnswerBank items={bank} search={search} adding={addingAnswer} onAddingDone={() => setAddingAnswer(false)} />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-7 pb-8 font-grotesk">
      {pending && (
        <ConfirmJob pending={pending} drafts={drafts} onChange={setPending} onCancel={() => setPending(null)} onSaved={() => setPending(null)} />
      )}

      <DraftList rows={rows} searching={!!search.trim()} onAdd={() => setShowAddModal(true)}>
        {(row) => (
          <Row
            key={row.key}
            row={row}
            brand={brandOf(row, dreamDomains)}
            busy={busy === row.key}
            onOpen={() => open(row)}
            onEdit={row.draft ? () => setEditDraft(row.draft!) : undefined}
            onApplied={() => markApplied(row)}
            onRemove={() => (row.draft ? setRemoving(row.draft) : unsave(row.saved!))}
          />
        )}
      </DraftList>

      {/* Where applied jobs went, and the drafts written for them */}
      {(moved > 0 || past.length > 0) && (
        <div className="-mt-3 font-plex text-xs text-melt-text3">
          <p>
            Applied jobs move to Applications.
            {moved > 0 && ` ${moved} moved this week.`}
            {past.length > 0 && (
              <>
                {" "}
                <button type="button" onClick={() => setShowPast((s) => !s)} className="underline decoration-melt-text3/40 underline-offset-2 hover:text-melt-text1">
                  {showPast ? "Hide" : "See"} past drafts ({past.length})
                </button>
              </>
            )}
          </p>
          {showPast && (
            <ul className="mt-2.5 overflow-hidden rounded-[14px] border border-melt-text1/[.12] bg-melt-paper font-grotesk">
              {past.map((d, i) => (
                <li key={d.id} className={cn(i > 0 && "border-t border-melt-hair")}>
                  <button
                    type="button"
                    onClick={() => setOpenId(d.id)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-melt-text1/[.025]"
                  >
                    <CompanyLogo name={d.company} {...brandOf(d, dreamDomains)} size={24} />
                    <span className="min-w-0 flex-1 truncate text-[13px] text-melt-text1">
                      {d.company} <span className="text-melt-text3">· {d.role || "Role not added"}</span>
                    </span>
                    <span className="font-plex text-[11px] text-melt-text3">
                      {d.appliedAt ? `Applied ${shortDate(toMs(d.appliedAt))}` : "Completed"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <DraftDialog
        isOpen={showAddModal || !!editDraft}
        editDraft={editDraft}
        onClose={() => {
          setShowAddModal(false);
          setEditDraft(null);
        }}
      />

      <RemoveDraftDialog draft={removing} onCancel={() => setRemoving(null)} onConfirm={() => removing && remove(removing)} />
    </div>
  );
}

// ---------------------------------------------------------------- dialogs

function RemoveDraftDialog({ draft, onCancel, onConfirm }: { draft: Draft | null; onCancel: () => void; onConfirm: () => void }) {
  if (!draft) return null;
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent showClose={false} className={cn(MELT_DIALOG, "max-w-[400px] px-7 pb-6 pt-7")}>
        <DialogTitle className="text-xl font-semibold tracking-[-.02em] text-melt-text1">Remove draft?</DialogTitle>
        <DialogDescription className="mt-2 text-sm leading-relaxed text-melt-text2">
          <span className="font-medium text-melt-text1">
            {draft.company}
            {draft.role ? `, ${draft.role}` : ""}
          </span>{" "}
          and any answers you wrote for it will be removed. This can’t be undone.
        </DialogDescription>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className={MELT_BUTTON_QUIET}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-11 items-center rounded-xl bg-[#A5492E] px-5 text-[15px] font-medium text-white transition-opacity hover:opacity-90"
          >
            Remove
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type DraftDialogProps = {
  isOpen: boolean;
  editDraft?: Draft | null;
  onClose: () => void;
};

/** Add a job by hand (with its questions), or edit a draft's details. */
function DraftDialog({ isOpen, editDraft, onClose }: DraftDialogProps) {
  const isEdit = !!editDraft;
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [deadline, setDeadline] = useState("");
  const [questions, setQuestions] = useState<string[]>([""]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCompany(editDraft?.company ?? "");
    setRole(editDraft?.role ?? "");
    setJobUrl(editDraft?.jobUrl ?? "");
    setDeadline(editDraft?.deadline ?? "");
    setQuestions([""]);
    setSaving(false);
  }, [isOpen, editDraft]);

  if (!isOpen) return null;

  const canSave = Boolean(company.trim() && role.trim());

  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user || !canSave || saving) return;
    setSaving(true);
    try {
      const details = {
        company: company.trim(),
        role: role.trim(),
        jobUrl: jobUrl.trim(),
        deadline,
      };
      if (isEdit && editDraft) {
        await updateDoc(doc(db, "users", user.uid, "writtenApplications", editDraft.id), details);
        onClose();
        return;
      }

      const cleaned = questions.map((q) => q.trim()).filter(Boolean);
      const appRef = await addDoc(collection(db, "users", user.uid, "writtenApplications"), {
        ...details,
        source: "manual",
        // With questions to write it's a draft already; without, it waits in Saved
        status: cleaned.length ? "in-progress" : "saved",
        createdAt: serverTimestamp(),
      });
      await Promise.all(
        cleaned.map((question, index) =>
          addDoc(collection(db, "users", user.uid, "applicationQuestions"), {
            applicationId: appRef.id,
            question,
            answer: "",
            order: index,
            createdAt: serverTimestamp(),
          })
        )
      );
      onClose();
    } catch (error) {
      console.error("Error saving draft:", error);
      alert("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showClose={false} className={cn(MELT_DIALOG, "flex max-h-[90vh] flex-col")}>
        <div className="flex items-start justify-between px-7 pb-2 pt-7">
          <div>
            <DialogTitle className="text-xl font-semibold tracking-[-.02em] text-melt-text1">{isEdit ? "Edit job" : "Add a job"}</DialogTitle>
            <DialogDescription className="mt-1 text-sm text-melt-text2">
              {isEdit ? "Update the job details." : "Save a job and the questions it asks."}
            </DialogDescription>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 grid h-8 w-8 place-items-center rounded-lg text-melt-text3 hover:bg-melt-text1/[.05] hover:text-melt-text1">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-7 py-4">
          <label className="flex flex-col gap-1.5">
            <span className={MELT_LABEL}>
              Company<span className={MELT_REQUIRED}>*</span>
            </span>
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company" className={MELT_INPUT} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className={MELT_LABEL}>
              Role<span className={MELT_REQUIRED}>*</span>
            </span>
            <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Product Manager Intern" className={MELT_INPUT} />
          </label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_170px]">
            <label className="flex flex-col gap-1.5">
              <span className={MELT_LABEL}>Link</span>
              <input type="url" value={jobUrl} onChange={(e) => setJobUrl(e.target.value)} placeholder="Job posting link" className={MELT_INPUT} />
            </label>
            <div className="flex flex-col gap-1.5">
              <span className={MELT_LABEL}>Deadline</span>
              <DatePicker value={deadline} onChange={setDeadline} className={MELT_INPUT} placeholder="No deadline" aria-label="Deadline" />
            </div>
          </div>

          {!isEdit && (
            <div className="rounded-[14px] border border-melt-hair bg-white/60 p-4">
              <div className="mb-2.5 flex items-center justify-between">
                <span className={MELT_LABEL}>Questions</span>
                <button
                  type="button"
                  onClick={() => setQuestions((prev) => [...prev, ""])}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[13px] font-medium text-melt-text2 hover:bg-melt-text1/[.05] hover:text-melt-text1"
                >
                  <Plus size={14} strokeWidth={1.75} />
                  Add question
                </button>
              </div>
              <div className="space-y-2">
                {questions.map((q, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      value={q}
                      onChange={(e) => setQuestions((prev) => prev.map((x, i) => (i === index ? e.target.value : x)))}
                      placeholder={`Question ${index + 1}`}
                      className={cn(MELT_INPUT, "h-10")}
                    />
                    {questions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setQuestions((prev) => prev.filter((_, i) => i !== index))}
                        aria-label="Remove question"
                        className="grid h-10 w-10 flex-none place-items-center rounded-[10px] text-melt-text3 hover:bg-melt-text1/[.05] hover:text-melt-text1"
                      >
                        <Trash2 size={15} strokeWidth={1.75} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-melt-hair px-7 py-4">
          <button type="button" onClick={onClose} className={MELT_BUTTON_QUIET}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !canSave} className={MELT_BUTTON}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Save job"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
